/**
 * Console platform → Module: danh mục module của ứng dụng và ma trận cấp
 * module cho từng tổ chức (client). Lưu theo từng tổ chức; module lõi (Tổng
 * quan, Hội thoại, Cài đặt) luôn bật.
 */
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Boxes, Lock, Save, RotateCcw, Search } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { usePlatformModules, useSetOrgModules, type ModuleDinhNghia, type ModuleToChuc } from '@/hooks/use-platform'

export function PlatformModulesPage() {
  const q = usePlatformModules()
  const luu = useSetOrgModules()
  const [tuKhoa, setTuKhoa] = useState('')
  /** Bản nháp theo tổ chức: orgId → tập key đang tick. */
  const [nhap, setNhap] = useState<Record<string, Set<string>>>({})

  useEffect(() => {
    if (!q.data) return
    setNhap(Object.fromEntries(q.data.toChuc.map((o) => [o.id, new Set(o.modules)])))
  }, [q.data])

  const danhMuc = q.data?.danhMuc ?? []
  const toChuc = useMemo(() => {
    const tk = tuKhoa.trim().toLowerCase()
    return (q.data?.toChuc ?? []).filter((o) => !tk || o.name.toLowerCase().includes(tk))
  }, [q.data, tuKhoa])

  const daDoi = (o: ModuleToChuc) => {
    const n = nhap[o.id]
    if (!n) return false
    const goc = new Set(o.modules)
    if (goc.size !== n.size) return true
    for (const k of n) if (!goc.has(k)) return true
    return false
  }

  const bat = (orgId: string, key: string, on: boolean) =>
    setNhap((prev) => {
      const s = new Set(prev[orgId] ?? [])
      if (on) s.add(key); else s.delete(key)
      return { ...prev, [orgId]: s }
    })

  const luuOrg = (o: ModuleToChuc) =>
    luu.mutate({ orgId: o.id, modules: [...(nhap[o.id] ?? [])] }, {
      onSuccess: () => toast.success(`Đã lưu module cho "${o.name}"`),
      onError: (e) => toast.error(apiError(e)),
    })

  if (q.isLoading) return <Loading label="Đang tải module..." />
  if (q.isError || !q.data) return <ErrorState message="Không tải được danh mục module" />

  return (
    <div className="space-y-6">
      <PageHeader
        title="Module"
        description="Danh mục module của ứng dụng và module được cấp cho từng tổ chức. Tổ chức không có module thì menu và API của module đó bị ẩn/chặn."
      />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base"><Boxes className="h-4 w-4" /> Danh mục module ({danhMuc.length})</CardTitle>
          <CardDescription>Module lõi luôn bật cho mọi tổ chức. "Số tổ chức" = đang được cấp.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {danhMuc.map((m) => <TheModule key={m.key} m={m} />)}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base">Cấp module theo tổ chức</CardTitle>
              <CardDescription>Tick module rồi bấm Lưu ở cuối dòng. Tổ chức đang dùng bộ mặc định có nhãn "mặc định".</CardDescription>
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input value={tuKhoa} onChange={(e) => setTuKhoa(e.target.value)} placeholder="Tìm tổ chức…" className="h-8 w-56 pl-8 text-xs" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-xs">
            <thead>
              <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3">Tổ chức</th>
                {danhMuc.map((m) => (
                  <th key={m.key} className="px-1 py-2 text-center font-medium" title={m.moTa}>
                    <span className="inline-flex items-center gap-1">{m.loi && <Lock className="h-3 w-3" />}{m.ten}</span>
                  </th>
                ))}
                <th className="py-2 pl-3 text-right">Lưu</th>
              </tr>
            </thead>
            <tbody>
              {toChuc.map((o) => {
                const n = nhap[o.id] ?? new Set(o.modules)
                const doi = daDoi(o)
                return (
                  <tr key={o.id} className={cn('border-b align-middle hover:bg-accent/30', o.status === 'suspended' && 'opacity-60')}>
                    <td className="py-2 pr-3">
                      <div className="font-medium">{o.name}</div>
                      <div className="flex gap-1 text-[10.5px] text-muted-foreground">
                        {o.macDinh && <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">mặc định</Badge>}
                        {o.status === 'suspended' && <Badge variant="destructive" className="px-1.5 py-0 text-[10px]">tạm khoá</Badge>}
                        <span>{n.size}/{danhMuc.length} module</span>
                      </div>
                    </td>
                    {danhMuc.map((m) => (
                      <td key={m.key} className="px-1 py-2 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-primary"
                          checked={m.loi || n.has(m.key)}
                          disabled={m.loi}
                          onChange={(e) => bat(o.id, m.key, e.target.checked)}
                          title={m.loi ? 'Module lõi — luôn bật' : m.ten}
                        />
                      </td>
                    ))}
                    <td className="py-2 pl-3 text-right">
                      <div className="inline-flex gap-1">
                        {doi && (
                          <Button size="sm" variant="ghost" className="h-7 px-2" title="Bỏ thay đổi" onClick={() => setNhap((p) => ({ ...p, [o.id]: new Set(o.modules) }))}>
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button size="sm" className="h-7" disabled={!doi || luu.isPending} onClick={() => luuOrg(o)}>
                          <Save className="h-3.5 w-3.5" /> Lưu
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
              {toChuc.length === 0 && (
                <tr><td colSpan={danhMuc.length + 2} className="py-6 text-center text-muted-foreground">Không có tổ chức nào khớp.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  )
}

function TheModule({ m }: { m: ModuleDinhNghia }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold">{m.ten}</span>
        {m.loi && <Badge variant="outline" className="gap-1 px-1.5 py-0 text-[10px]"><Lock className="h-3 w-3" /> lõi</Badge>}
        {!m.batMacDinh && <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">tắt mặc định</Badge>}
        <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">{m.soToChuc} tổ chức</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{m.moTa}</p>
      <p className="mt-1 font-mono text-[10.5px] text-muted-foreground">{m.key} · {m.duongDan}</p>
    </div>
  )
}

/** Khối tick module dùng ở trang chi tiết tổ chức. */
export function OrgModulesCard({ orgId }: { orgId: string }) {
  const q = usePlatformModules()
  const luu = useSetOrgModules()
  const org = q.data?.toChuc.find((o) => o.id === orgId)
  const [nhap, setNhap] = useState<Set<string> | null>(null)
  useEffect(() => { if (org) setNhap(new Set(org.modules)) }, [org?.id, org?.modules.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  if (q.isLoading || !q.data || !org || !nhap) return <Loading label="Đang tải module..." />
  const goc = new Set(org.modules)
  const doi = goc.size !== nhap.size || [...nhap].some((k) => !goc.has(k))

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base"><Boxes className="h-4 w-4" /> Module được cấp {org.macDinh && <Badge variant="secondary" className="text-[10px]">đang dùng mặc định</Badge>}</CardTitle>
        <CardDescription>Bỏ tick là tổ chức không thấy menu và bị chặn API của module đó. Module lõi luôn bật.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-2 sm:grid-cols-2">
          {q.data.danhMuc.map((m) => (
            <label key={m.key} className={cn('flex cursor-pointer items-start gap-2 rounded-md border p-2.5 text-sm', m.loi && 'cursor-default opacity-80')}>
              <input
                type="checkbox" className="mt-0.5 h-4 w-4 accent-primary"
                checked={m.loi || nhap.has(m.key)} disabled={m.loi}
                onChange={(e) => setNhap((p) => { const s = new Set(p ?? []); if (e.target.checked) s.add(m.key); else s.delete(m.key); return s })}
              />
              <span className="min-w-0">
                <span className="flex items-center gap-1 font-medium">{m.ten}{m.loi && <Lock className="h-3 w-3 text-muted-foreground" />}</span>
                <span className="block text-xs text-muted-foreground">{m.moTa}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          {doi && <Button variant="outline" size="sm" onClick={() => setNhap(new Set(org.modules))}><RotateCcw className="h-3.5 w-3.5" /> Bỏ thay đổi</Button>}
          <Button size="sm" disabled={!doi || luu.isPending} onClick={() => luu.mutate({ orgId, modules: [...nhap] }, { onSuccess: () => toast.success('Đã lưu module'), onError: (e) => toast.error(apiError(e)) })}>
            <Save className="h-3.5 w-3.5" /> Lưu module
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
